import { AppLayout } from '@/components/layout/AppLayout'
import { AtlasPage } from '@/components/ui/AtlasPage'
import { PageTitle } from '@/components/ui/PageTitle'
import { Separator } from '@/components/ui/Separator'
import { PageNumber } from '@/components/ui/PageNumber'
import { Button } from '@/components/ui/Button'
import { useForm, usePage, router } from '@inertiajs/react'
import { useEffect, useState, useCallback } from 'react'
import type { SharedProps } from '@/types/inertia'
import { useConfirm } from '@/hooks/useConfirm'
import { ConfirmModal } from '@/components/ui/ConfirmModal'
import { IconSelect } from '@/components/ui/IconSelect'
import { SUNLIGHT_ICONS, DIFFICULTY_ICONS, STATUS_ICONS, LIGHT_LEVEL_ICONS } from '@/lib/icons'

interface Photo {
  id: number
  url: string
  thumbnail_url: string
  filename: string
}

interface PlantData {
  id: number
  name: string
  species_name: string | null
  pot_diameter_cm: number | null
  location_id: number | null
  watering_frequency_days: number | null
  watering_amount_ml: number | null
  sunlight_preference: string | null
  difficulty_level: string | null
  status: string | null
  acquired_at: string | null
  notes: string | null
  photos: Photo[]
}

interface Props {
  plant: PlantData
  locations: { id: number; name: string; light_level: string | null }[]
}

const sunlightOptions = [
  { value: '', label: '— Wybierz —' },
  { value: 'full_sun', label: 'Jasne bezpośrednie' },
  { value: 'indirect', label: 'Jasne rozproszone' },
  { value: 'partial_shade', label: 'Półcień' },
  { value: 'shade', label: 'Cień' },
]

const difficultyOptions = [
  { value: '', label: '— Wybierz —' },
  { value: 'easy', label: 'Łatwa' },
  { value: 'medium_difficulty', label: 'Średnia' },
  { value: 'hard', label: 'Trudna' },
]

const statusOptions = [
  { value: '', label: '— Wybierz —' },
  { value: 'healthy', label: 'Zdrowa' },
  { value: 'needs_attention', label: 'Wymaga uwagi' },
  { value: 'sick', label: 'Chora' },
  { value: 'dormant', label: 'Uśpiona' },
]

function PlantsEdit({ plant, locations }: Props) {
  const { errors } = usePage<SharedProps>().props
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([])
  const { pending, confirm, handleConfirm, handleCancel } = useConfirm()

  const { data, setData, put, processing } = useForm({
    plant: {
      name: plant.name || '',
      species_name: plant.species_name || '',
      pot_diameter_cm: plant.pot_diameter_cm || '',
      location_id: plant.location_id || '',
      watering_frequency_days: plant.watering_frequency_days || '',
      watering_amount_ml: plant.watering_amount_ml || '',
      sunlight_preference: plant.sunlight_preference || '',
      difficulty_level: plant.difficulty_level || '',
      status: plant.status || 'healthy',
      acquired_at: plant.acquired_at || '',
      notes: plant.notes || '',
      photos: [] as File[],
    },
  })

  const setField = useCallback((field: string, value: unknown) => {
    setData('plant', { ...data.plant, [field]: value })
  }, [data.plant, setData])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    put(`/plants/${plant.id}`, {
      forceFormData: true,
    })
  }

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    setData('plant', { ...data.plant, photos: files })
    const previews = files.map((f) => URL.createObjectURL(f))
    setPhotoPreviews(previews)
  }

  const handleRemovePhoto = (photoId: number) => {
    confirm('Usunąć to zdjęcie?', () => router.delete(`/plants/${plant.id}/remove_photo?photo_id=${photoId}`))
  }

  // Cleanup previews
  useEffect(() => {
    return () => photoPreviews.forEach((url) => URL.revokeObjectURL(url))
  }, [photoPreviews])

  return (
    <AtlasPage>
      <PageTitle title="Edycja rośliny" subtitle={plant.name} />
      <Separator />

      {/* Existing photos */}
      {plant.photos.length > 0 && (
        <div className="mb-md">
          <p className="font-label text-xs text-frame uppercase tracking-wide mb-sm">
            Obecne zdjęcia
          </p>
          <div className="flex flex-wrap gap-sm">
            {plant.photos.map((photo) => (
              <div key={photo.id} className="relative group">
                <img
                  src={photo.thumbnail_url}
                  alt={photo.filename}
                  className="h-24 w-24 object-cover border border-frame/30"
                />
                <button
                  type="button"
                  onClick={() => handleRemovePhoto(photo.id)}
                  className="absolute -top-1 -right-1 h-5 w-5 bg-danger text-white text-xs rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Usuń zdjęcie"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-md">
        <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
          <FormField label="Nazwa" error={errors?.name}>
            <input
              type="text"
              value={data.plant.name}
              onChange={(e) => setField('name', e.target.value)}
              className="form-input"
              required
            />
          </FormField>

          <FormField label="Gatunek" error={errors?.species_name}>
            <input
              type="text"
              value={data.plant.species_name}
              onChange={(e) => setField('species_name', e.target.value)}
              className="form-input"
              placeholder="np. Monstera deliciosa"
            />
          </FormField>

          <FormField label="Średnica doniczki (cm)">
            <input
              type="number"
              value={data.plant.pot_diameter_cm}
              onChange={(e) => setField('pot_diameter_cm', e.target.value)}
              className="form-input"
              min="1"
              step="0.1"
            />
          </FormField>

          <FormField label="Miejsce">
            <IconSelect
              value={String(data.plant.location_id)}
              onChange={(v) => setField('location_id', v)}
              options={locations.map((l) => ({ value: String(l.id), label: l.name, iconKey: l.light_level || '' }))}
              iconMap={LIGHT_LEVEL_ICONS}
              placeholder="— Brak —"
            />
          </FormField>

          <FormField label="Częstotliwość podlewania (dni)" error={errors?.watering_frequency_days}>
            <input
              type="number"
              value={data.plant.watering_frequency_days}
              onChange={(e) => setField('watering_frequency_days', e.target.value)}
              className="form-input"
              min="1"
            />
          </FormField>

          <FormField label="Ilość wody (ml)" error={errors?.watering_amount_ml}>
            <input
              type="number"
              value={data.plant.watering_amount_ml}
              onChange={(e) => setField('watering_amount_ml', e.target.value)}
              className="form-input"
              min="1"
            />
          </FormField>

          <FormField label="Nasłonecznienie">
            <IconSelect value={data.plant.sunlight_preference} onChange={(v) => setField('sunlight_preference', v)} options={sunlightOptions} iconMap={SUNLIGHT_ICONS} />
          </FormField>

          <FormField label="Trudność">
            <IconSelect value={data.plant.difficulty_level} onChange={(v) => setField('difficulty_level', v)} options={difficultyOptions} iconMap={DIFFICULTY_ICONS} />
          </FormField>

          <FormField label="Status">
            <IconSelect value={data.plant.status} onChange={(v) => setField('status', v)} options={statusOptions} iconMap={STATUS_ICONS} />
          </FormField>

          <FormField label="Data nabycia">
            <input
              type="date"
              value={data.plant.acquired_at}
              onChange={(e) => setField('acquired_at', e.target.value)}
              className="form-input"
            />
          </FormField>
        </div>

        <FormField label="Notatki">
          <textarea
            value={data.plant.notes}
            onChange={(e) => setField('notes', e.target.value)}
            className="form-input min-h-[6rem]"
          />
        </FormField>

        <FormField label="Dodaj nowe zdjęcia">
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handlePhotoChange}
            className="block w-full font-label text-sm text-frame file:mr-sm file:border file:border-frame file:bg-paper-dark file:px-sm file:py-xs file:text-xs file:text-ink file:font-label"
          />
          {photoPreviews.length > 0 && (
            <div className="flex flex-wrap gap-sm mt-sm">
              {photoPreviews.map((url, i) => (
                <img key={i} src={url} alt="" className="h-20 w-20 object-cover border border-frame/30" />
              ))}
            </div>
          )}
        </FormField>

        <Separator />

        <div className="flex items-center justify-center gap-md">
          <Button type="submit" disabled={processing}>
            Zapisz zmiany
          </Button>
          <Button href={`/plants/${plant.id}`} variant="secondary">
            Anuluj
          </Button>
        </div>
      </form>

      <PageNumber number={5} />
      {pending && <ConfirmModal message={pending.message} onConfirm={handleConfirm} onCancel={handleCancel} />}
    </AtlasPage>
  )
}

function FormField({
  label,
  error,
  children,
}: {
  label: string
  error?: string[]
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="block font-label text-xs text-frame uppercase tracking-wide mb-xs">
        {label}
      </label>
      {children}
      {error && (
        <p className="text-xs text-danger mt-xs">{Array.isArray(error) ? error.join(', ') : error}</p>
      )}
    </div>
  )
}

PlantsEdit.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
export default PlantsEdit
